import {
  EnrollmentInvestmentExperienceCategory,
  EnrollmentInvestmentObjectiveCategory,
  useGetEnrollmentJointInvestmentGoalsData,
  useSubmitEnrollmentJointInvestmentGoalsData,
} from '@egw/api'
import { Button, GenericError, ProgressBar, RadioGroup, Tag } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { EnrollmentJointLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useEffect, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { UserIcon } from '@heroicons/react/16/solid'
import { WEALTH_ROUTES } from '@egw/constants'
import { PageHeader } from '../../../components'
import { ROUTES } from '../../../constants/routes/routes-generic'
import { ROUTES as JOINT_ROUTES } from '../../../constants/routes/routes-joint'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { useHandleEnrollmentErrors } from '../../../utils/joint/api-errors/enrollment-error-handler'
import { sortInvestmentOptionGoals } from '../../../utils/investments/sort-investment-option-goals'
import { sortInvestmentOptionExperiences } from '../../../utils/investments/sort-investment-option-experiences'

const log = new Logger('Enrollment - Joint - InvestmentGoals')

interface FormData {
  objective: string
  experience: string
}

export const InvestmentGoalsPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const { data, isLoading, error } = useGetEnrollmentJointInvestmentGoalsData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointInvestmentGoalsData()

  // Log initial data loading errors
  useEffect(() => {
    if (error) {
      log.error('Error loading enrollment joint investment goals data', error)
    }
  }, [error])

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint investment goals form', submitError)
    }
  }, [submitError])
  const progress = data?.activePerson?.progress
  const profileName = data?.activePerson?.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''

  // Protect route
  useValidateRoute(data?.activePerson?.path)

  // Error handler for enrollment errors
  const handleEnrollmentErrors = useHandleEnrollmentErrors('Joint - InvestmentGoals')

  // AUTO GENERATED FORM HANDLERS START
  const {
    control,
    handleSubmit,
    formState: { errors: _formErrors, isValid },
    reset,
  } = useForm({
    defaultValues: {
      objective: '',
      experience: '',
    },
  })

  useEffect(() => {
    if (data?.activePerson?.investmentObjective && data.activePerson.investmentExperience) {
      reset({
        objective: data.activePerson.investmentObjective,
        experience: data.activePerson.investmentExperience,
      })
    }
  }, [data, reset])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData

    try {
      const response = await mutate({
        activePerson: {
          investmentObjective: formData.objective as EnrollmentInvestmentObjectiveCategory,
          investmentExperience: formData.experience as EnrollmentInvestmentExperienceCategory,
        },
      })

      // Check for API errors in the response
      if (response && handleEnrollmentErrors(response)) {
        return
      }

      if (response?.activePerson?.path) {
        void navigate(response.activePerson.path)
      } else if (!response?.activePerson?.path) {
        log.error('No path returned from backend, redirecting to error page')
        void navigate(ROUTES.ERROR.path)
      }
    } catch (e) {
      log.error('Error submitting investment goals form', e)
    }
  })
  // AUTO GENERATED FORM HANDLERS END

  return (
    <EnrollmentJointLayout title={t('joint.investment-goals.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          {/* AUTO GENERATED COMPONENTS START*/}
          {profileName ? (
            <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
              {profileName}
            </Tag>
          ) : null}
          <PageHeader
            title={t('joint.investment-goals.title')}
            // backPath={`${WEALTH_ROUTES.ENROLLMENT.path}/${JOINT_ROUTES.FINANCES.path}`}
          />
          <ProgressBar className="mb-md" progressPercentage={progress} showAnimation />
          <p className="typo-primary-base mb-xl text-text-meta">{t('joint.investment-goals.description')}</p>
          <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col h-full">
            <p className="typo-primary-base-semibold mb-sm">{t('joint.investment-goals.objective_title')}</p>
            <Controller
              name="objective"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { ...restField } }) => (
                <RadioGroup
                  {...restField}
                  options={sortInvestmentOptionGoals(
                    Object.values(EnrollmentInvestmentObjectiveCategory).map((item) => ({
                      value: item,
                      label: t(`joint.investment-goals.investment_objective.${item}`),
                    })),
                  )}
                  name="objective"
                  className="!py-xs mb-xl"
                  testId="objective-radio"
                />
              )}
            />

            <p className="typo-primary-base-semibold mb-sm">{t('joint.investment-goals.experience_title')}</p>
            <Controller
              name="experience"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { ...restField } }) => (
                <RadioGroup
                  {...restField}
                  options={sortInvestmentOptionExperiences(
                    Object.values(EnrollmentInvestmentExperienceCategory).map((item) => ({
                      value: item,
                      label: t(`joint.investment-goals.investment_experience.${item}`),
                    })),
                  )}
                  name="experience"
                  className="!py-xs mb-xl"
                  testId="experience-radio"
                />
              )}
            />

            <div className="mt-auto md:mt-0">
              <Button
                type="submit"
                className="w-full md:w-min"
                isDisabled={!isValid}
                isLoading={isSubmitting}
                testId="enrollment-continue-button"
              >
                {t('common.continue')}
              </Button>
            </div>
          </form>
          {/* AUTO GENERATED COMPONENTS END*/}
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
